'use client';
import clsx from 'clsx';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { Popover } from 'radix-ui';
import type React from 'react';
import { createContext, type ReactElement, type ReactNode, useContext, useState } from 'react';
import styles from './PopoverMenu.module.scss';

const PopoverMenuContext = createContext<(() => void) | undefined>(undefined);

type Side = 'top' | 'right' | 'bottom' | 'left';
type Align = 'start' | 'center' | 'end';
type Width = 'auto' | 'anchorWidth';

type PopoverMenuAllowedChildren = ReactElement<
  React.ComponentProps<typeof PopoverMenuLink> | React.ComponentProps<typeof PopoverMenuButton>
>;

interface PopoverMenuProps {
  trigger: ReactNode;
  children:
    | PopoverMenuAllowedChildren
    | PopoverMenuAllowedChildren[]
    | (PopoverMenuAllowedChildren | PopoverMenuAllowedChildren[])[];
  sideOffset?: number;
  collisionPadding?: number;
  side?: Side;
  align?: Align;
  width?: Width;
  ariaLabel?: string;
  headerMenu?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export const PopoverMenu: React.FC<PopoverMenuProps> = ({
  trigger,
  children,
  sideOffset,
  collisionPadding = 8,
  side = 'bottom',
  align = 'center',
  width = 'auto',
  ariaLabel,
  headerMenu = false,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
}) => {
  const [open, setOpen] = useState(false);
  const isControlled = controlledOpen !== undefined && controlledOnOpenChange !== undefined;
  const actualOpen = isControlled ? controlledOpen : open;
  const setActualOpen = isControlled ? controlledOnOpenChange! : setOpen;

  const closeMenu = () => setActualOpen(false);

  return (
    <PopoverMenuContext.Provider value={closeMenu}>
      <Popover.Root open={actualOpen} onOpenChange={setActualOpen}>
        <Popover.Trigger asChild>{trigger}</Popover.Trigger>
        <Popover.Portal>
          <Popover.Content
            className={clsx(styles.foldout, {
              [styles.headerMenu]: headerMenu,
              [styles.foldoutAnchorWidth]: width === 'anchorWidth',
            })}
            sideOffset={sideOffset}
            side={side}
            align={align}
            collisionPadding={headerMenu ? 0 : collisionPadding}
          >
            <nav className={styles.nav} aria-label={ariaLabel}>
              <ul className={styles.list}>{children}</ul>
            </nav>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </PopoverMenuContext.Provider>
  );
};

interface PopoverMenuItemProps {
  children: ReactNode;
}
const PopoverMenuItem: React.FC<PopoverMenuItemProps> = ({ children }) => (
  <li className={styles.item}>{children}</li>
);

interface PopoverMenuLinkProps {
  href: string;
  label: string;
  title: string;
  icon?: ReactNode;
  testId?: string;
  notificationCount?: number;
}
export const PopoverMenuLink: React.FC<PopoverMenuLinkProps> = ({
  href,
  label,
  title,
  icon,
  testId,
  notificationCount,
}) => {
  const closeMenu = useContext(PopoverMenuContext);
  const t = useTranslations('other.common');

  const hasNotification = notificationCount !== undefined && notificationCount > 0;
  const _title = hasNotification
    ? `${title} (${t('notificationTitle', { count: notificationCount })})`
    : title;

  return (
    <PopoverMenuItem>
      <Link
        href={href}
        title={_title}
        className={styles.link}
        data-testid={testId}
        onClick={() => {
          setTimeout(() => closeMenu?.(), 0);
        }}
      >
        {icon}
        {label}
        {hasNotification && <span className={styles.notificationCount}>{notificationCount}</span>}
      </Link>
    </PopoverMenuItem>
  );
};

interface PopoverMenuButtonProps {
  label: string;
  title: string;
  icon?: ReactNode;
  testId?: string;
  isActive?: boolean;
  onClick?: () => void;
}
export const PopoverMenuButton: React.FC<PopoverMenuButtonProps> = ({
  label,
  title,
  icon,
  testId,
  isActive,
  onClick,
}) => {
  const closeMenu = useContext(PopoverMenuContext);
  return (
    <PopoverMenuItem>
      <button
        onClick={(_e) => {
          onClick?.();
          closeMenu?.();
        }}
        title={title}
        className={styles.button}
        data-testid={testId}
        type="button"
        aria-current={isActive}
      >
        {icon}
        {label}
      </button>
    </PopoverMenuItem>
  );
};
